import React, { forwardRef, useImperativeHandle, useCallback, memo } from 'react';
import LapDisplay from './LapDisplay';
import { useStopwatchContext } from '../contexts';
// Remove individual style imports since they're now in main.css
import { WorkoutControls, DistanceButtons, PaceIndicators, TimerDisplays } from './workout';
import { StopwatchProps, StopwatchRef } from '../types/stopwatch';

const Stopwatch = memo(
  forwardRef<StopwatchRef, StopwatchProps>(({ title }, ref) => {
    // Use the stopwatch context for all state and actions
    const {
      mainTime,
      lapTime,
      segmentTime,
      isRunning,
      laps,
      startTimer,
      pauseTimer,
      resetTimer,
      recordLap,
      completeWorkout,
      resetAndStart,
      currentSegmentDistance,
      recordSegment,
      effectiveSegmentsByLap,
      calculatePace,
      formatPace,
      lastCompletedSession,
    } = useStopwatchContext();

    // Expose methods to parent through ref
    useImperativeHandle(ref, () => ({
      resetAndStart,
      resetTimer,
      pauseTimer,
      recordLap,
    }));

    // Memoized callbacks for child components
    const handleStartStop = useCallback(() => {
      return isRunning ? pauseTimer() : startTimer();
    }, [isRunning, pauseTimer, startTimer]);

    const handleRecordSegment = useCallback(
      (distance: number) => {
        return recordSegment(distance);
      },
      [recordSegment]
    );

    // Create a wrapper for recordLap to ensure it's called without arguments
    const handleLap = useCallback(() => {
      return recordLap();
    }, [recordLap]);

    // Determine the stopwatch state class modifier
    const stateClass = isRunning ? 'stopwatch--running' : 'stopwatch--paused';

    return (
      <div className={`stopwatch ${stateClass}`} data-testid="stopwatch-container">
        <TimerDisplays
          mainTime={mainTime}
          lapTime={lapTime}
          segmentTime={segmentTime}
          isRunning={isRunning}
          title={title}
        />

        <PaceIndicators />

        <DistanceButtons
          isRunning={isRunning}
          onRecordSegment={handleRecordSegment}
          currentSegmentDistance={currentSegmentDistance}
        />

        <WorkoutControls
          isRunning={isRunning}
          onStartStop={handleStartStop}
          onLap={handleLap}
          onComplete={completeWorkout}
          lapNumber={laps.length}
        />

        <LapDisplay
          laps={laps}
          effectiveSegmentsByLap={effectiveSegmentsByLap}
          sessionSegments={lastCompletedSession?.segments}
          calculatePace={calculatePace}
          formatPace={formatPace}
        />
      </div>
    );
  })
);

// Add display name
Stopwatch.displayName = 'Stopwatch';

// Default props are handled by TypeScript default parameters
export default Stopwatch;
